import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { formatYAxisValue } from '../../Components/DrillDown/formatters';
import { LineChartSkeleton } from '../../Components/Skeletons/ChartSkeletons';

export type DadoIcms = {
  mes: string;
  "ICMS a Recolher": number;
  "PERIODO_DRILL_DOWN"?: string;
  "SPED_FISCAL_ID"?: number | string;
  "SPED_FISCAL_IDS"?: string;
};

interface EvolucaoIcmsChartProps {
  endpoint: string;
  title: string;
  onBarClick?: (params: { periodo?: string; sped_fiscal_id?: number | string }) => void;
}

const EvolucaoIcmsChart: React.FC<EvolucaoIcmsChartProps> = ({ endpoint, title, onBarClick }) => {
  const [dados, setDados] = useState<DadoIcms[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const fetchData = async () => {
        setLoading(true);
        
        try {
            const token = document.cookie
                .split('; ')
                .find(row => row.startsWith('authTokenMHP='))
                ?.split('=')[1];

            const response = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/${endpoint}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 422) {
                    setDados([]);
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const mapItem = (item: any) => {
                const rawSpedIds = item["SPED_FISCAL_IDS"];
                const normalizedSpedIds = Array.isArray(rawSpedIds)
                    ? rawSpedIds
                    : rawSpedIds != null
                    ? [rawSpedIds]
                    : [];

                const spedFiscalIdsCsv = normalizedSpedIds
                    .map((id: number | string) => String(id).trim())
                    .filter(Boolean)
                    .join(',');

                return {
                    mes: item["MÊS"],
                    "ICMS a Recolher": item["VALOR ICMS A RECOLHER"],
                    "PERIODO_DRILL_DOWN": item["PERIODO_DRILL_DOWN"],
                    "SPED_FISCAL_ID": item["SPED_FISCAL_ID"],
                    "SPED_FISCAL_IDS": spedFiscalIdsCsv || undefined
                };
            };

              const transformado = Array.isArray(data.data)
                  ? data.data.map(mapItem)
                  : Object.values(data.data ?? {}).map(mapItem);

                setDados(transformado);
        } catch (error) {
            console.error('Erro ao carregar dados de ICMS:', error);
            setDados([]);
        } finally {
            setLoading(false);
        }
    };

    fetchData();
  }, []);

  return (
    <Card sx={{ borderRadius: 3, p: 2, height: 620 }}>
      <CardContent>
        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#555555' }}>
          {title}
        </Typography>
        
        {loading ? (
          <LineChartSkeleton height={570} showTitle={false} />
        ) : !dados || dados.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={500}>
            <Typography variant="body1" color="text.secondary">
              📊 Nenhum dado disponível
            </Typography>
          </Box>
        ) : (
          <div style={{ height: 500 }}>
            <ResponsiveBar
            valueFormat={(value) =>
              new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL"
              }).format(value as number)
            }
            data={dados}
            keys={["ICMS a Recolher"]}
            indexBy="mes"
            margin={{ top: 30, right: 30, bottom: 80, left: 70 }}
            padding={0.4}
            groupMode="grouped"
            colors="#F44336"
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisBottom={{
              tickRotation: -45,
              legend: "Mês",
              legendPosition: "middle",
              legendOffset: 50
            }}
            axisLeft={{
              legend: "Valor (R$)",
              legendPosition: "middle",
              legendOffset: -50,
              format: formatYAxisValue
            }}
            enableLabel={false}
              onClick={(bar) => {
                if (onBarClick) {
                const dataPoint = bar.data as DadoIcms;
                const spedFiscalId = dataPoint["SPED_FISCAL_ID"];
                const spedFiscalIds = (dataPoint["SPED_FISCAL_IDS"] ?? "").trim();
                const periodoFallback = dataPoint["PERIODO_DRILL_DOWN"] || "";

                  let spedParam: string | number | undefined;
                if (spedFiscalIds.length > 0) {
                  spedParam = spedFiscalIds;
                } else if (spedFiscalId !== undefined && spedFiscalId !== null && String(spedFiscalId).trim() !== '') {
                    spedParam = spedFiscalId;
                  }

                  if (spedParam || periodoFallback) {
                    onBarClick({
                      sped_fiscal_id: spedParam,
                      periodo: periodoFallback || undefined
                    });
                  } else {
                    console.error("SPED_FISCAL_ID e PERIODO_DRILL_DOWN não encontrados nos dados da barra:", bar.data);
                  }
                }
              }}
            tooltip={({ value }) => (
              <strong>{new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(value)}</strong>
            )}
          />
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EvolucaoIcmsChart;
